import React, { useEffect, useState } from "react";
import { Ban } from "lucide-react";
import { format, parseISO } from "date-fns";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import usePermissions from "../../hooks/usePermissions";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

// "Não contatar" na ficha do contato (Fase 0 do HANDOFF-sdr).
//
// A regra é assimétrica e a tela segue: marcar fica à mão de quem atende
// (o cliente pediu por telefone, por mensagem), retirar só aparece para quem
// tem `contacts:unsuppress` — um falso positivo é revisado, não desfeito por
// qualquer um. A IA de voz marca sozinha quando o cliente pede na ligação.

const quando = (valor) => {
	try {
		return format(parseISO(valor), "dd/MM/yyyy 'às' HH:mm");
	} catch {
		return "";
	}
};

const quemMarcou = (item) =>
	item.channel === "voz" && !item.createdBy
		? "Pedido na ligação, registrado pela IA de voz"
		: `Marcado por ${item.createdBy?.name || "alguém da equipe"}`;

const NaoContatar = ({ contactId }) => {
	const { temPermissao } = usePermissions();
	const podeRetirar = temPermissao("contacts:unsuppress");

	const [historico, setHistorico] = useState(null);
	const [marcando, setMarcando] = useState(false);
	const [retirando, setRetirando] = useState(false);
	const [motivo, setMotivo] = useState("");
	const [salvando, setSalvando] = useState(false);

	// Troca de contato remonta o componente (`key` na ficha): o estado do
	// anterior nunca aparece no próximo.
	useEffect(() => {
		if (!contactId) return undefined;

		let valeAinda = true;

		api
			.get(`/contacts/${contactId}/suppression`)
			.then(({ data }) => valeAinda && setHistorico(data || []))
			// Sem a lista a ficha segue: o bloqueio vale no servidor do mesmo jeito.
			.catch(() => valeAinda && setHistorico([]));

		// A IA de voz marca durante a ligação, com a ficha aberta.
		const socket = openSocket();

		socket.on("contact-suppression", (data) => {
			if (String(data?.contactId) !== String(contactId)) return;

			api
				.get(`/contacts/${contactId}/suppression`)
				.then(({ data: lista }) => valeAinda && setHistorico(lista || []))
				.catch(() => {});
		});

		return () => {
			valeAinda = false;
			socket.disconnect();
		};
	}, [contactId]);

	if (!historico) return null;

	const ativa = historico.find((item) => !item.removedAt);
	const ultimaRetirada = historico.find((item) => item.removedAt);

	const recarregar = async () => {
		const { data } = await api.get(`/contacts/${contactId}/suppression`);
		setHistorico(data || []);
	};

	const marcar = async () => {
		setSalvando(true);
		try {
			await api.post(`/contacts/${contactId}/suppression`, {
				evidence: motivo.trim(),
			});
			setMarcando(false);
			setMotivo("");
			await recarregar();
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	const retirar = async () => {
		setSalvando(true);
		try {
			await api.delete(`/contacts/${contactId}/suppression`);
			setRetirando(false);
			await recarregar();
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	if (ativa) {
		return (
			<div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
				<p className="flex items-center gap-2 text-sm font-semibold text-destructive">
					<Ban className="h-4 w-4" />
					Não contatar
				</p>
				<p className="mt-1 text-xs text-muted-foreground">
					{quemMarcou(ativa)} em {quando(ativa.createdAt)}. Ligações,
					campanhas, agendamentos, follow-ups e automações não chegam a este
					contato. Se ele escrever, a conversa segue normal.
				</p>
				{ativa.evidence && (
					<blockquote className="mt-2 border-l-2 border-destructive/40 pl-2 text-xs italic text-foreground">
						{ativa.evidence}
					</blockquote>
				)}

				{podeRetirar &&
					(retirando ? (
						<div className="mt-3 space-y-2">
							<p className="text-xs text-foreground">
								Retirar a marcação? O contato volta a poder receber ligações.
							</p>
							<div className="flex gap-2">
								<Button size="sm" disabled={salvando} onClick={retirar}>
									Retirar
								</Button>
								<Button
									size="sm"
									variant="ghost"
									disabled={salvando}
									onClick={() => setRetirando(false)}
								>
									Cancelar
								</Button>
							</div>
						</div>
					) : (
						<Button
							size="sm"
							variant="outline"
							className="mt-3"
							onClick={() => setRetirando(true)}
						>
							Retirar “não contatar”
						</Button>
					))}
			</div>
		);
	}

	return (
		<div className="rounded-xl border bg-card p-4">
			{marcando ? (
				<div className="space-y-2">
					<p className="flex items-center gap-2 text-sm font-semibold">
						<Ban className="h-4 w-4 text-muted-foreground" />
						Marcar como não contatar
					</p>
					<p className="text-xs text-muted-foreground">
						Ligações, campanhas, agendamentos, follow-ups e automações deixam
						de chegar a este contato, e o que estava agendado é cancelado.
						Só quem tem permissão consegue retirar depois.
					</p>
					<Textarea
						rows={2}
						value={motivo}
						onChange={(e) => setMotivo(e.target.value)}
						placeholder="O que o cliente disse, ou por que marcar (opcional)."
					/>
					<div className="flex gap-2">
						<Button
							size="sm"
							variant="destructive"
							disabled={salvando}
							onClick={marcar}
						>
							Marcar
						</Button>
						<Button
							size="sm"
							variant="ghost"
							disabled={salvando}
							onClick={() => setMarcando(false)}
						>
							Cancelar
						</Button>
					</div>
				</div>
			) : (
				<div className="flex items-center justify-between gap-2">
					<p className="text-xs text-muted-foreground">
						{ultimaRetirada
							? `“Não contatar” retirado por ${
									ultimaRetirada.removedBy?.name || "alguém da equipe"
								} em ${quando(ultimaRetirada.removedAt)}.`
							: "O cliente pediu para não ser procurado?"}
					</p>
					<Button
						size="sm"
						variant="outline"
						className="shrink-0"
						onClick={() => setMarcando(true)}
					>
						<Ban className="h-3.5 w-3.5" />
						Não contatar
					</Button>
				</div>
			)}
		</div>
	);
};

export default NaoContatar;
